import React, { useState, useEffect, useRef, useCallback } from "react";
import "./PenaltyShootout.css";

const PenaltyShootout = ({ onBack, isPaused }) => {
    const [score, setScore] = useState(0);
    const [gameOver, setGameOver] = useState(false);
    const [gameStarted, setGameStarted] = useState(false);
    const [feedback, setFeedback] = useState("");
    const [shots, setShots] = useState(0);

    const CAN_W = 400;
    const CAN_H = 600;
    const TOTAL_SHOTS = 10;
    
    // Goalie logic
    const gRange = { x: 50, w: 300, y: 180 };

    const game = useRef({
        ball: { x: CAN_W/2, y: 500, vx: 0, vy: 0, active: false, initialX: CAN_W/2, initialY: 500 },
        goalie: { x: CAN_W/2 - 25, targetX: CAN_W/2 - 25, speed: 4 },
        swipeStart: null,
        lastFrame: 0
    });

    const canvasRef = useRef(null);

    const update = useCallback((time) => {
        if (!gameStarted || gameOver) return;
        if (isPaused) {
            game.current.lastFrame = time;
            requestAnimationFrame(update);
            return;
        }
        const dt = (time - game.current.lastFrame) / 16;
        game.current.lastFrame = time;

        const ball = game.current.ball;
        const goalie = game.current.goalie;

        // Move Goalie
        goalie.x += (goalie.targetX - goalie.x) * 0.1 * dt;
        if (Math.abs(goalie.x - goalie.targetX) < 5) {
            goalie.targetX = 100 + Math.random() * (CAN_W - 200);
        }

        // Move Ball
        if (ball.active) {
            ball.x += ball.vx * dt;
            ball.y += ball.vy * dt;
            ball.vx *= 0.99; ball.vy *= 0.99; // Air resistance

            // Collision with goalie
            if (ball.y < 250 && ball.y > 180) {
                if (ball.x > goalie.x && ball.x < goalie.x + 50) {
                    setFeedback("SAVED! 👐");
                    ball.active = false;
                    setTimeout(() => resetBall(), 1000);
                }
            }

            // Goal check
            if (ball.y < 180) {
                if (ball.x > 80 && ball.x < 320) {
                    setScore(s => s + 1);
                    setFeedback("GOAL!!! ⚽💎");
                } else {
                    setFeedback("MISSED! ❌");
                }
                ball.active = false;
                setTimeout(() => resetBall(), 1000);
            }

            // Out check
            if (ball.x < 0 || ball.x > CAN_W || ball.y < 0) {
                ball.active = false;
                setTimeout(() => resetBall(), 1000);
            }
        }

        if (shots >= TOTAL_SHOTS && !ball.active) setGameOver(true);

        render();
        requestAnimationFrame(update);
    }, [gameStarted, gameOver, shots, isPaused]);

    const resetBall = () => {
        setFeedback("");
        const ball = game.current.ball;
        ball.x = ball.initialX;
        ball.y = ball.initialY;
        ball.vx = 0; ball.vy = 0;
        ball.active = false;
    };

    const handleSwipeEnd = (e) => {
        if (!gameStarted || game.current.ball.active || shots >= TOTAL_SHOTS) return;
        const rect = canvasRef.current.getBoundingClientRect();
        const ex = (e.changedTouches ? e.changedTouches[0].clientX : e.clientX) - rect.left;
        const ey = (e.changedTouches ? e.changedTouches[0].clientY : e.clientY) - rect.top;
        
        const dx = (ex - (game.current.ball.x / (CAN_W/rect.width)));
        const dy = (ey - (game.current.ball.y / (CAN_W/rect.width)));
        
        // Physics kick
        game.current.ball.vx = dx * 0.05;
        game.current.ball.vy = dy * 0.1;
        game.current.ball.active = true;
        setShots(s => s + 1);
    };

    const render = () => {
        const ctx = canvasRef.current?.getContext("2d");
        if (!ctx) return;
        ctx.clearRect(0, 0, CAN_W, CAN_H);

        // Grass
        ctx.fillStyle = "#166534";
        ctx.fillRect(0, 0, CAN_W, CAN_H);
        
        // Goal Net
        ctx.strokeStyle = "#fff";
        ctx.lineWidth = 5;
        ctx.strokeRect(80, 50, 240, 130);
        ctx.fillStyle = "rgba(255,255,255,0.1)";
        ctx.fillRect(80, 50, 240, 130);
        
        // Goalie
        ctx.fillStyle = "#ef4444";
        ctx.fillRect(game.current.goalie.x, 180, 50, 60);
        ctx.fillStyle = "#ffdbac";
        ctx.arc(game.current.goalie.x + 25, 170, 15, 0, Math.PI*2);
        ctx.fill();

        // Ball
        const ball = game.current.ball;
        ctx.beginPath();
        ctx.fillStyle = "#fff";
        ctx.shadowBlur = 10; ctx.shadowColor = "#ccc";
        ctx.arc(ball.x, ball.y, 15, 0, Math.PI*2);
        ctx.fill();
        ctx.lineWidth = 2; ctx.strokeStyle = "#000"; ctx.stroke();
    };

    useEffect(() => {
        if (gameStarted) {
            game.current.lastFrame = performance.now();
            requestAnimationFrame(update);
        }
    }, [gameStarted, update]);

    return (
        <div className="ps_container" onMouseUp={handleSwipeEnd} onTouchEnd={handleSwipeEnd}>
            <div className="te_header">
                <button onClick={onBack} className="te_back">← Hub</button>
                <div className="te_score">GOALS: {score} | SHOTS: {shots}/{TOTAL_SHOTS}</div>
            </div>
            <canvas ref={canvasRef} width={CAN_W} height={CAN_H} />
            
            {feedback && <div className="ps_feedback">{feedback}</div>}

            {!gameStarted && (
                <div className="te_overlay">
                    <h1>PENALTY <span>HERO</span></h1>
                    <p>Swipe up from the ball to shoot!</p>
                    <button onClick={() => setGameStarted(true)}>PLAY MATCH</button>
                </div>
            )}
            {gameOver && (
                <div className="te_overlay">
                    <h1>FULL TIME 🏁</h1>
                    <p>Total Goals: {score}</p>
                    <button onClick={() => window.location.reload()}>REMATCH</button>
                </div>
            )}
        </div>
    );
};

export default PenaltyShootout;
